import React, { useState, useEffect } from "react";
import {
    Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, Radio, RadioGroup, FormControlLabel, FormControl,
    Autocomplete, CircularProgress, Snackbar, Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import ClearIcon from "@mui/icons-material/Clear";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { getCurrentFYear } from "../utils/financialYear";
import {
    FB, FB_DARK, FB_LIGHT, FB_MID, BG, BORDER, T1, T2,
    G200, G300, G500, G700, G900, WHITE,
    btnGhostSx, btnPrimarySx, btnOutlineSx, inputSx, dgSx,
} from "./ReportDetailsConstants";

const getTodayDate = () => new Date().toISOString().split("T")[0];

// Indian FY runs April 1 -> March 31. Returns the FY's last date (March 31)
// as "YYYY-MM-DD", e.g. today in FY 2026-27 -> "2027-03-31".
const getFYEndDate = () => {
    const today = new Date();
    const month = today.getMonth(); // 0 = Jan … 3 = Apr
    const year = today.getFullYear();
    const fyEndYear = month >= 3 ? year + 1 : year; // April–December -> next year's March 31
    return `${fyEndYear}-03-31`;
};

// ── DepreciationReportFilterDialog — same visual design as SmartFilterDialog ──
// (General Ledger's popup): NAVY-free header with left accent bar + title +
// subtitle + close icon, uppercase field labels, G200 inputs, Preview chips,
// Cancel/Get Data footer actions.
// Fields: As On Date, Asset's Code (party autocomplete, defaults to "ALL"),
// and a single-select radio: "As Per I.T. Law ?" / "As Per Company Law ?".
function DepreciationReportFilterDialog({ open, onClose, connectionString, companyId, onOk, onCreateJV, postingJV }) {
    const [asOnDate, setAsOnDate] = useState(getFYEndDate());
    const [assetValue, setAssetValue] = useState(null); // null => "ALL"
    const [assetInput, setAssetInput] = useState("");
    const [assetOptions, setAssetOptions] = useState([]);
    const [assetLoading, setAssetLoading] = useState(false);
    const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);
    const [lawType, setLawType] = useState("company"); // "it" | "company"

    // Reset to defaults every time the dialog is opened
    useEffect(() => {
        if (open) {
            setAsOnDate(getFYEndDate());
            setAssetValue(null);
            setAssetInput("");
            setLawType("company");
        }
    }, [open]);

    // Search "Asset's Code" as user types, or list default options on open —
    // same /api/company-search pattern used by TDSMaster/DepreciationMaster.
    useEffect(() => {
        if (!assetDropdownOpen && (!assetInput || assetInput.trim() === "")) {
            setAssetOptions([]);
            return;
        }

        let active = true;
        const timer = setTimeout(async () => {
            setAssetLoading(true);
            try {
                const res = await axios.get(`${connectionString}/api/company-search`, {
                    params: { field: "FirmName", search: assetInput, companyId },
                });
                if (active) {
                    const rows = Array.isArray(res.data?.data) ? res.data.data : [];
                    setAssetOptions(rows);
                }
            } catch (err) {
                if (active) setAssetOptions([]);
            } finally {
                if (active) setAssetLoading(false);
            }
        }, 300);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [assetInput, assetDropdownOpen, companyId, connectionString]);

    const handleOk = () => {
        onOk({
            asOnDate,
            // assetCode carries clientmaster.id (i.e. depreciationmaster.PartyId) of the
            // selected asset, or null to mean "ALL assets" — null is intentional here,
            // not a bug, whenever the Asset's Code field is left at its "ALL" default.
            assetCode: assetValue?.id || null,
            assetCodeLabel: assetValue?.FirmName || "ALL",
            lawType,
        });
        onClose();
    };

    // "Create a JV of Depreciation Client?" — no backend endpoint exists yet,
    // so this only surfaces the captured filters for now (same as OK), via
    // onCreateJV. Wire the actual JV-creation call once the backend is ready.
    const handleCreateJV = () => {
        onCreateJV({
            asOnDate,
            // Same "ALL means null" semantics as handleOk above.
            assetCode: assetValue?.id || null,
            assetCodeLabel: assetValue?.FirmName || "ALL",
            lawType,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: "10px", border: `1px solid ${G300}`, boxShadow: "0 8px 40px rgba(0,0,0,0.12)" } }}
        >
            {/* ── Dialog Title ── */}
            <DialogTitle sx={{ p: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.75, borderBottom: `1px solid ${G300}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 3, height: 22, background: FB, borderRadius: 2 }} />
                        <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: G900, lineHeight: 1 }}>Depreciation Report</Typography>
                            <Typography sx={{ fontSize: 11, color: G500, mt: "2px" }}>Configure report parameters</Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: G500, "&:hover": { background: G200 } }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* ── Dialog Content ── */}
            <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                    {/* ── As On Date (+ Post Depreciation JV, top-right) ── */}
                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "6px" }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, letterSpacing: ".3px", textTransform: "uppercase" }}>
                                As On Date
                            </Typography>
                            <Button
                                onClick={handleCreateJV}
                                disabled={postingJV}
                                size="small"
                                startIcon={postingJV ? <CircularProgress size={12} sx={{ color: FB }} /> : null}
                                sx={{
                                    color: FB,
                                    borderColor: FB_MID,
                                    border: "1px solid",
                                    borderRadius: "6px",
                                    background: "transparent",
                                    height: 28,
                                    minWidth: "auto",
                                    px: 1.25,
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                    lineHeight: 1,
                                    "&:hover": { background: FB_LIGHT, borderColor: FB },
                                }}
                            >
                                {postingJV ? "Posting…" : "Post Depreciation JV"}
                            </Button>
                        </Box>
                        <TextField type="date" fullWidth size="small" value={asOnDate} onChange={(e) => setAsOnDate(e.target.value)} sx={inputSx} />
                    </Box>

                    {/* ── Divider ── */}
                    <Box sx={{ height: "1px", background: BORDER, mx: -0.5 }} />

                    {/* ── Asset's Code — party autocomplete, defaults to "ALL" ── */}
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, mb: "6px", letterSpacing: ".3px", textTransform: "uppercase" }}>
                            Asset's Code
                        </Typography>
                        <Autocomplete
                            fullWidth
                            options={assetOptions}
                            loading={assetLoading}
                            open={assetDropdownOpen}
                            onOpen={() => setAssetDropdownOpen(true)}
                            onClose={() => setAssetDropdownOpen(false)}
                            getOptionLabel={(opt) => opt?.FirmName || ""}
                            isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                            value={assetValue}
                            inputValue={assetInput}
                            onInputChange={(_, newInput) => setAssetInput(newInput)}
                            onChange={(_, newValue) => setAssetValue(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="ALL"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: <SearchIcon sx={{ mr: 1, color: T2, fontSize: 18 }} />,
                                        endAdornment: (
                                            <>
                                                {assetLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={inputSx}
                                />
                            )}
                        />
                    </Box>

                    {/* ── As Per I.T. Law ? / As Per Company Law ? ── */}
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, mb: "6px", letterSpacing: ".3px", textTransform: "uppercase" }}>
                            Law Type
                        </Typography>
                        <FormControl>
                            <RadioGroup value={lawType} onChange={(e) => setLawType(e.target.value)}>
                                <FormControlLabel
                                    value="company"
                                    control={<Radio size="small" sx={{ color: FB_MID, "&.Mui-checked": { color: FB } }} />}
                                    label={
                                        <Typography sx={{ fontSize: 14, fontStyle: "italic", fontWeight: 600, color: T1 }}>
                                            As Per Company Law ?
                                        </Typography>
                                    }
                                />
                                <FormControlLabel
                                    value="it"
                                    control={<Radio size="small" sx={{ color: FB_MID, "&.Mui-checked": { color: FB } }} />}
                                    label={
                                        <Typography sx={{ fontSize: 14, fontStyle: "italic", fontWeight: 600, color: "#D32F2F" }}>
                                            As Per I.T. Law ?
                                        </Typography>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* ── Preview ── */}
                    {asOnDate && (
                        <Box sx={{ background: FB_LIGHT, border: `1px solid ${G300}`, borderRadius: "10px", p: "10px 14px", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: G500, textTransform: "uppercase", letterSpacing: ".3px" }}>Preview</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "10px", py: "3px", background: WHITE, border: `1px solid ${G300}`, borderRadius: "20px" }}>
                                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: FB }} />
                                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: FB }}>As On {asOnDate}</Typography>
                                </Box>
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "10px", py: "3px", background: WHITE, border: `1px solid ${G300}`, borderRadius: "20px" }}>
                                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: FB }} />
                                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: FB }}>Asset: {assetValue?.FirmName || "ALL"}</Typography>
                                </Box>
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "10px", py: "3px", background: WHITE, border: `1px solid ${G300}`, borderRadius: "20px" }}>
                                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: FB }} />
                                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: FB }}>{lawType === "it" ? "As Per I.T. Law" : "As Per Company Law"}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* ── Dialog Actions ── */}
            <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
                <Button onClick={onClose} sx={btnGhostSx} fullWidth>
                    Cancel
                </Button>
                <Button onClick={handleOk} startIcon={<SearchIcon sx={{ fontSize: 15 }} />} sx={btnPrimarySx} fullWidth>
                    Get Data
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── DepreciationReport — sidebar-selectable report entry ──────────────────────
// Mirrors StockReport/LedgerDetails' initialReportType/openFilterTrigger prop
// contract so AccountReports.js can wire it into the sidebar the same way as
// every other report. No backend call yet — this is the filter popup only,
// per request; the results view/API will be added once the backend logic is
// provided.
export default function DepreciationReport({ connectionString, pdfString, initialReportType, openFilterTrigger }) {
    const { user, selectedCompany } = useAuth();
    const companyId = selectedCompany?.CompId;
    const AppRegID = selectedCompany?.AppRegID || null;
    const userId = user?.UserId || null;
    const fYear = getCurrentFYear();
    const branchId = selectedCompany?.BranchId;

    const [filterOpen, setFilterOpen] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [postingJV, setPostingJV] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);
    const [reportRows, setReportRows] = useState([]);
    const [totalDepreciation, setTotalDepreciation] = useState(0);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    // Confirm dialog shown when the backend reports an existing Depreciation JV
    // for this company/financial year (409 confirmationRequired response) —
    // holds the filters to re-post with confirmOverride:true once the user says Yes.
    const [overrideConfirm, setOverrideConfirm] = useState({ open: false, message: "", filters: null });

    // Same trigger pattern as StockReport/LedgerDetails: sidebar click
    // increments openFilterTrigger, which re-opens this report's own dialog.
    useEffect(() => {
        if (openFilterTrigger > 0) {
            setFilterOpen(true);
        }
    }, [openFilterTrigger]);

    // "Get Data" — pulls g_ledgerbook transactions for the matching
    // depreciationmaster asset(s) up to As On Date and computes each row's
    // depreciation via the ported Crystal Reports "PurcDeprAmt" formula
    // (day-table + Company/I.T. Law branching + Debit/Credit sign flip).
    const handleOk = async (filters) => {
        setAppliedFilters(filters);
        setReportLoading(true);
        try {
            const res = await axios.get(`${connectionString}/api/Depreciation/Report`, {
                params: {
                    asOnDate: filters.asOnDate,
                    assetCode: filters.assetCode || undefined,
                    lawType: filters.lawType,
                    companyId,
                },
            });

            const data = res.data;
            setReportRows(Array.isArray(data?.data) ? data.data : []);
            setTotalDepreciation(Number(data?.totalDepreciation) || 0);
            setSnackbar({
                open: true,
                message: data?.message
                    ? `${data.message} (${data?.rowsComputed ?? 0} row(s), total ${Number(data?.totalDepreciation) || 0})`
                    : "Depreciation report generated.",
                severity: "success",
            });
        } catch (err) {
            setReportRows([]);
            setTotalDepreciation(0);
            const errMsg = err?.response?.data?.message || err.message || "Failed to generate Depreciation Report";
            setSnackbar({ open: true, message: errMsg, severity: "error" });
        } finally {
            setReportLoading(false);
        }
    };

    // "Post Depreciation JV" — pulls every depreciationmaster row for this
    // company (or a single asset if one was picked), computes each asset's
    // depreciation via OpBal * rate% (Company/I.T. Law per the radio), and
    // posts one Journal Voucher (g_ledgermast -> bankcashreceiptmaster ->
    // jvdts -> g_ledgerbook), same as the ported VB.NET cmdDepriciation_Click.
    const handleCreateJV = async (filters, confirmOverride = false) => {
        if (postingJV) return;
        setPostingJV(true);
        try {
            const res = await axios.post(`${connectionString}/api/Depreciation/PostJV`, {
                asOnDate: filters.asOnDate,
                assetCode: filters.assetCode || null,
                lawType: filters.lawType,
                companyId,
                GCompId: AppRegID,
                userId,
                fYear,
                branchId,
                AppRegID,
                ...(confirmOverride ? { confirmOverride: true } : {}),
            });

            const data = res.data;
            setSnackbar({
                open: true,
                message: data?.message
                    ? `${data.message} (Voucher: ${data.voucherNo}, Total: ${data.totalDebit})`
                    : "Depreciation JV posted successfully.",
                severity: "success",
            });
        } catch (err) {
            // Backend responds 409 + confirmationRequired:true when a Depreciation
            // JV already exists for this company/financial year — open a confirm
            // dialog instead of showing it as a plain error.
            if (err?.response?.status === 409 && err?.response?.data?.confirmationRequired) {
                setOverrideConfirm({
                    open: true,
                    message: err.response.data.message || "A Depreciation JV has already been posted for this company/financial year. Do you want to override the existing Depreciation JV?",
                    filters,
                });
                return;
            }

            const errMsg = err?.response?.data?.message || err.message || "Failed to post Depreciation JV";
            setSnackbar({
                open: true,
                message: errMsg,
                severity: "error",
            });
        } finally {
            setPostingJV(false);
        }
    };

    // "Export PDF" — sends the request the same way General Ledger's
    // handleExportPDF does: POST ${pdfString}/api/ledger-pdf/<docId> with
    // { docId, userId, data: {...}, company: {...} }, responseType: "blob",
    // then opens the returned PDF blob in a new tab. Uses its own docId
    // ("depreciation-report") so the PDF microservice can be given/already has
    // a dedicated template for it, keyed off this docId — the `entries` shape
    // (clientName, date, particulars, bookName, DocNo, debit, credit, balance,
    // balanceType, clientTotalDebit/clientTotalCredit, depreciationAmt) mirrors
    // General Ledger's contract so the same renderer can be reused/extended.
    const handleExportPDF = async () => {
        if (!appliedFilters || reportRows.length === 0 || pdfLoading) return;

        try {
            setPdfLoading(true);

            const printDate = getTodayDate();

            // Running balance per row, same convention as General Ledger's PDF
            // (Debit increases balance, Credit decreases it) — Dep.Amt itself
            // isn't a ledger balance, so this only drives the printed running
            // total column, not the depreciation figures.
            let runningBalance = 0;
            const entries = reportRows.map((row, idx) => {
                const debit = Number(row.Debit) || 0;
                const credit = Number(row.Credit) || 0;
                runningBalance += debit - credit;

                return {
                    clientName: row.FirmName || appliedFilters.assetCodeLabel,
                    clientCode: "",
                    date: row.DocDate,
                    particulars: row.BookName || "",
                    remark: row.Remark || "",
                    BookName: row.BookName || "",
                    salePurchaseAC: "—",
                    DocNo: row.DocNo || "—",
                    debit,
                    credit,
                    balance: Math.abs(runningBalance),
                    balanceType: runningBalance >= 0 ? "Dr" : "Cr",
                    depreciationAmt: row.PurcDeprAmt,
                    clientTotalDebit: totalDepreciation,
                    clientTotalCredit: 0,
                    srNo: idx + 1,
                };
            });

            const pdfPayload = {
                docId: "depreciation-report",
                userId: user?.UserId || "usr1",
                data: {
                    reportType: "Depreciation Report",
                    accountName: `Depreciation Report - ${appliedFilters.assetCodeLabel}`,
                    fromDate: appliedFilters.asOnDate,
                    toDate: appliedFilters.asOnDate,
                    printDate,
                    entries,
                    clientSummaries: [{
                        firmName: appliedFilters.assetCodeLabel,
                        clientCode: "",
                        openingBalance: 0,
                        openingType: "Dr",
                        closingBalance: totalDepreciation,
                        closingType: totalDepreciation >= 0 ? "Dr" : "Cr",
                        totalDebit: totalDepreciation,
                        totalCredit: 0,
                        entries: entries.length,
                    }],
                    totalDebit: totalDepreciation,
                    totalCredit: 0,
                    totalClients: 1,
                },
                company: selectedCompany
                    ? {
                        CompId: selectedCompany.CompId,
                        CompanyName: selectedCompany.CompanyName || "",
                        Address: selectedCompany.Address || "",
                        MobileNo: selectedCompany.MobileNo || "",
                        GSTIN: selectedCompany.GSTNo || "",
                    }
                    : {},
            };

            // 🔍 Log the exact payload being sent to the PDF microservice, same
            // spot General Ledger would log it if it did — useful for verifying
            // the reshaped `entries` before the request goes out.
            console.log("📄 [Depreciation Report] Export PDF payload:", pdfPayload);

            const pdfResp = await axios.post(
                `${pdfString}/api/ledger-pdf/depreciation-report`,
                pdfPayload,
                { responseType: "blob" }
            );

            const w = window.open(
                URL.createObjectURL(new Blob([pdfResp.data], { type: "application/pdf" })),
                "_blank"
            );
            if (w) w.focus();
        } catch (err) {
            console.error("PDF Error:", err);
            setSnackbar({ open: true, message: "Failed to generate PDF", severity: "error" });
        } finally {
            setPdfLoading(false);
        }
    };

    const handleConfirmOverride = () => {
        const filters = overrideConfirm.filters;
        setOverrideConfirm({ open: false, message: "", filters: null });
        if (filters) handleCreateJV(filters, true);
    };

    const handleCancelOverride = () => {
        setOverrideConfirm({ open: false, message: "", filters: null });
    };

    // DataGrid columns for the results table — same column set/labels as the
    // earlier plain-table version, just adapted to DataGrid's column shape.
    const gridColumns = [
        { field: "FirmName", headerName: "Asset", flex: 1.2, minWidth: 140 },
        { field: "DocDate", headerName: "Doc Date", flex: 0.8, minWidth: 110 },
        { field: "DocNo", headerName: "Doc No", flex: 0.9, minWidth: 110 },
        { field: "BookName", headerName: "Book", flex: 0.8, minWidth: 100 },
        { field: "Debit", headerName: "Debit", type: "number", flex: 0.8, minWidth: 100 },
        { field: "Credit", headerName: "Credit", type: "number", flex: 0.8, minWidth: 100 },
        {
            field: "PurcDeprAmt", headerName: "Depreciation Amt", type: "number", flex: 1, minWidth: 140,
            renderCell: (params) => (
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: params.value < 0 ? "#D32F2F" : T1 }}>
                    {params.value}
                </Typography>
            ),
        },
    ];

    return (
        <Box sx={{ background: G200, display: "flex", flexDirection: "column", minHeight: "100%" }}>
            {/* ── ERP Header — same design as General Ledger ── */}
            <Box sx={{ background: WHITE, borderBottom: `1px solid ${G300}`, display: "flex", alignItems: "stretch", flexShrink: 0, minHeight: 58 }}>
                <Box sx={{ width: 4, background: FB, flexShrink: 0 }} />
                <Box sx={{ display: "flex", alignItems: "center", flex: 1, justifyContent: "space-between", px: { xs: 2, sm: 3 }, py: 1.25, gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, background: FB_LIGHT, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <TrendingDownOutlinedIcon sx={{ fontSize: 20, color: FB }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: G900, lineHeight: 1.2 }}>Depreciation Report</Typography>
                            <Typography sx={{ fontSize: 11, color: G500, mt: "1px" }}>
                                {appliedFilters ? `As On ${appliedFilters.asOnDate} · ${appliedFilters.assetCodeLabel}` : ""}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        {/* Export PDF — shown before the Filter button, same order as General Ledger */}
                        {appliedFilters && reportRows.length > 0 && (
                            <Button onClick={handleExportPDF} disabled={pdfLoading} startIcon={pdfLoading ? <CircularProgress size={13} sx={{ color: FB }} /> : <PictureAsPdfOutlinedIcon sx={{ fontSize: 15 }} />} sx={btnOutlineSx}>
                                Export PDF
                            </Button>
                        )}
                        <Button onClick={() => setFilterOpen(true)} startIcon={<TuneIcon sx={{ fontSize: 15 }} />} sx={btnPrimarySx}>
                            Smart Filter
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* ── Toolbar: filter tags row (same design as General Ledger) ── */}
            {appliedFilters && (
                <Box sx={{ background: WHITE, borderBottom: `1px solid ${G300}`, flexShrink: 0 }}>
                    <Box sx={{ px: { xs: 2, sm: 3 }, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, background: G200, borderBottom: `1px solid ${G300}` }}>
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                            <Typography sx={{ fontSize: 11, color: G500, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".4px", mr: 0.5 }}>Filter:</Typography>
                            {[
                                `As On ${appliedFilters.asOnDate}`,
                                `Asset: ${appliedFilters.assetCodeLabel}`,
                                appliedFilters.lawType === "it" ? "As Per I.T. Law" : "As Per Company Law",
                            ].filter(Boolean).map(tag => (
                                <Box key={tag} sx={{ px: 1.25, py: "2px", background: FB_LIGHT, borderRadius: "4px", border: `1px solid ${G300}` }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: FB }}>{tag}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Button onClick={() => setAppliedFilters(null)} size="small" startIcon={<ClearIcon sx={{ fontSize: 12 }} />}
                            sx={{ color: G700, border: `1px solid ${G300}`, background: "transparent", borderRadius: "6px", fontSize: 11, fontWeight: 500, textTransform: "none", px: 1.25, height: 26, "&:hover": { background: G200 } }}>
                            Clear
                        </Button>
                    </Box>
                </Box>
            )}

            {reportLoading ? (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: "3rem" }}>
                    <CircularProgress sx={{ color: FB }} />
                </Box>
            ) : appliedFilters && reportRows.length > 0 ? (
                // ── Results grid — one row per g_ledgerbook transaction, with the
                // computed PurcDeprAmt (Crystal Reports formula ported verbatim) —
                // same DataGrid styling (dgSx) as General Ledger. ──
                <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ background: FB_LIGHT, border: `1px solid ${G300}`, borderRadius: "8px", px: 2, py: 1.25, mb: 1.5, display: "inline-flex", flexDirection: "column" }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: G500, textTransform: "uppercase", letterSpacing: ".4px" }}>Total Depreciation</Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: FB }}>{totalDepreciation}</Typography>
                    </Box>
                    <Box sx={{ background: WHITE, border: `1px solid ${G300}`, borderRadius: "8px", overflow: "hidden" }}>
                        <DataGrid
                            rows={reportRows}
                            columns={gridColumns}
                            getRowId={(row) => row.gLId}
                            autoHeight
                            rowHeight={44}
                            disableRowSelectionOnClick
                            pageSizeOptions={[25, 50, 100]}
                            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                            sx={dgSx}
                        />
                    </Box>
                </Box>
            ) : (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
                    <Box sx={{ textAlign: "center", maxWidth: 380 }}>
                        <Box sx={{ width: 64, height: 64, borderRadius: "16px", background: FB_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
                            <AssessmentOutlinedIcon sx={{ fontSize: 32, color: FB }} />
                        </Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: T1, mb: 1 }}>Depreciation Report</Typography>
                        <Typography sx={{ fontSize: 13, color: T2, lineHeight: 1.7, mb: 2 }}>
                            {appliedFilters
                                ? "No depreciation transactions found for the selected filters."
                                : "Configure the As On Date, Asset's Code, and applicable law to view the depreciation report."}
                        </Typography>
                        <Button onClick={() => setFilterOpen(true)} startIcon={<TuneIcon sx={{ fontSize: 14 }} />} sx={btnPrimarySx}>
                            Configure & Run Report
                        </Button>

                        {appliedFilters && (
                            <Box sx={{ mt: 3, background: WHITE, border: `1px solid ${G300}`, borderRadius: "10px", p: "10px 14px", textAlign: "left" }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: G500, mb: "6px", textTransform: "uppercase", letterSpacing: ".5px" }}>Last Applied Filters</Typography>
                                <Typography sx={{ fontSize: 12, color: T1 }}>As On Date: {appliedFilters.asOnDate}</Typography>
                                <Typography sx={{ fontSize: 12, color: T1 }}>Asset's Code: {appliedFilters.assetCodeLabel}</Typography>
                                <Typography sx={{ fontSize: 12, color: T1 }}>
                                    {appliedFilters.lawType === "it" ? "As Per I.T. Law" : "As Per Company Law"}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            <DepreciationReportFilterDialog
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                connectionString={connectionString}
                companyId={companyId}
                onOk={handleOk}
                onCreateJV={handleCreateJV}
                postingJV={postingJV}
            />

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Confirm override of an existing Depreciation JV (backend 409 response) */}
            <Dialog open={overrideConfirm.open} onClose={handleCancelOverride} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: T1 }}>Depreciation JV Already Exists</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13, color: T2, lineHeight: 1.6 }}>
                        {overrideConfirm.message}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCancelOverride} sx={btnGhostSx} disabled={postingJV}>
                        No
                    </Button>
                    <Button onClick={handleConfirmOverride} sx={btnPrimarySx} disabled={postingJV}>
                        {postingJV ? <CircularProgress size={16} sx={{ color: WHITE }} /> : "Yes, Override"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
